import ky, { type KyInstance, HTTPError } from 'ky';
import { acquireAccessToken } from '../auth/msalClient';
import { addBreadcrumb } from '../breadcrumbs/breadcrumbs';
import { reportError } from '../errors/errorReporter';

// Our edge functions are served by the dedicated `outlook-edge-functions` Coolify app
// (supabase/edge-runtime Docker image with our functions baked in), reachable at
// VITE_OUTLOOK_FUNCTIONS_URL. Each function is at `${VITE_OUTLOOK_FUNCTIONS_URL}/outlook-<name>`.
const FUNCTIONS_BASE = import.meta.env.VITE_OUTLOOK_FUNCTIONS_URL;

let client: KyInstance | null = null;

export function getApiClient(): KyInstance {
  if (client) return client;
  client = ky.create({
    prefix: FUNCTIONS_BASE,
    timeout: 30_000,
    hooks: {
      // Ky 2: each hook receives a state object with { request, options, ... }.
      beforeRequest: [
        async ({ request }) => {
          try {
            const token = await acquireAccessToken();
            request.headers.set('Authorization', `Bearer ${token}`);
          } catch (err) {
            addBreadcrumb({
              category: 'auth',
              message: 'token acquisition failed before request',
              data: { url: request.url },
            });
            // Token-acquisition failures throw before any HTTP response, so the
            // beforeError hook (HTTPError-only) never sees them and the global
            // unhandledrejection handler doesn't fire (TanStack catches it).
            // Report explicitly so the real MSAL/NAA error code lands in the DB.
            const e = err as { name?: string; message?: string; errorCode?: string };
            reportError({
              error: { name: e?.name ?? 'AuthError', message: e?.message ?? String(err) },
              tags: ['auth', 'token-acquisition', e?.errorCode ? `msal:${e.errorCode}` : 'msal:unknown'],
              lastApiCall: { url: request.url },
            });
            throw err;
          }
          addBreadcrumb({
            category: 'api.request',
            message: `${request.method} ${request.url}`,
          });
        },
      ],
      afterResponse: [
        ({ request, response }) => {
          addBreadcrumb({
            category: 'api.response',
            message: `${response.status} ${request.url}`,
          });
        },
      ],
      beforeError: [
        async ({ error }) => {
          if (error instanceof HTTPError) {
            // ky 2 consumes the error response body ITSELF to populate
            // `error.data`, so `error.response.json()/.text()/.clone()` all
            // throw "body stream already read". `data` is the parsed object for
            // a JSON content-type, the raw text otherwise, and undefined for an
            // empty/unparseable body — it is set before this hook runs.
            //
            // Reading the response here instead used to throw, and the throw was
            // swallowed by the surrounding catch: every server message was lost,
            // so a 500 surfaced as ky's bare "Request failed with status code
            // 500" and the DB got no response excerpt.
            const data: unknown = error.data;
            let responseExcerpt: string | undefined;
            if (typeof data === 'string') {
              responseExcerpt = data.slice(0, 500);
            } else if (data !== undefined) {
              try {
                responseExcerpt = JSON.stringify(data)?.slice(0, 500);
              } catch {
                // non-serialisable body — leave the excerpt out
              }
            }
            // Surface the server's { error } message on error.message so UIs
            // that render it (dialogs, toasts) show the real reason — e.g. the
            // 409 dedup text — instead of ky's generic "Request failed with
            // status code 409".
            const serverMessage = (data as { error?: unknown } | null | undefined)?.error;
            if (typeof serverMessage === 'string' && serverMessage.trim()) {
              error.message = serverMessage;
            }
            reportError({
              error,
              tags: ['api', `status:${error.response.status}`],
              lastApiCall: {
                url: error.request.url,
                status: error.response.status,
                responseExcerpt,
              },
            });
          }
          return error;
        },
      ],
    },
  });
  return client;
}
